import { CartAddEvent, CartErrorEvent, DiscountUpdateEvent } from '@theme/events';
import { fetchConfig, prefersReducedMotion } from '@theme/utilities';

const BUTTON_SELECTOR = '[data-button-action-type]';
const SCRIPT_SELECTOR = '[data-button-action-script]';
const FEEDBACK_SELECTOR = '[data-button-action-feedback]';
const compiledActions = new WeakMap();
const animatedButtons = new WeakSet();

initializeButtonAnimations();

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest(BUTTON_SELECTOR) : null;

  if (!(button instanceof HTMLAnchorElement)) return;

  event.preventDefault();
  void runButtonAction(button, event);
});

// Action anchors marked role="button" must answer Space like a native button (Enter already
// fires click on an <a href>). preventDefault stops Space from scrolling the page.
document.addEventListener('keydown', (event) => {
  if (event.key !== ' ' && event.key !== 'Spacebar') return;
  const button = event.target instanceof Element ? event.target.closest(BUTTON_SELECTOR) : null;
  if (!(button instanceof HTMLAnchorElement) || button.getAttribute('role') !== 'button') return;

  event.preventDefault();
  if (!event.repeat) button.click();
});

async function runButtonAction(button, event) {
  if (button.dataset.buttonActionBusy === 'true') return;

  const feedback = getFeedbackNode(button);
  const sourceId = button.id || button.closest('[id]')?.id || 'custom-button-action';
  const actionType = button.dataset.buttonActionType || '';

  try {
    button.dataset.buttonActionBusy = 'true';
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    clearFeedback(feedback);

    const config = getButtonActionConfig(button);
    const context = createContext(button, event, feedback, sourceId);

    if (actionType === 'custom_js') {
      const source = sanitizeActionSource(config.customJavascript);
      if (!source) {
        throw new Error('Add JavaScript to the button action field before using Run JavaScript.');
      }

      const actionRunner = getActionRunner(button, source);
      await actionRunner(context);
    } else {
      await runBuiltInAction(actionType, config, context);
    }
  } catch (error) {
    const message = formatErrorMessage(error);

    button.dispatchEvent(new CartErrorEvent(sourceId, message, message, null));
    showFeedback(feedback, message, 'error');
    console.error('Button action failed:', error);
  } finally {
    delete button.dataset.buttonActionBusy;
    button.removeAttribute('aria-busy');
    button.classList.remove('is-loading');
  }
}

function getButtonActionConfig(button) {
  const script = button.nextElementSibling;

  if (!(script instanceof HTMLScriptElement) || !script.matches(SCRIPT_SELECTOR)) {
    return {};
  }

  try {
    return JSON.parse(script.textContent || '{}');
  } catch (error) {
    // Don't fail silently: a malformed config means gifts/discount/plan are dropped,
    // and the button would degrade without any signal. Surface it so it's debuggable.
    console.error('[Super Button] Failed to parse action config JSON:', error);
    return {};
  }
}

function sanitizeActionSource(rawSource) {
  if (typeof rawSource !== 'string') return '';

  const source = rawSource
    .replace(/\u0000/g, '')
    .replace(/^\s*<script[^>]*>/i, '')
    .replace(/<\/script>\s*$/i, '')
    .trim();

  if (source.length > 8000) {
    throw new Error('This button action is too long. Please shorten the JavaScript and try again.');
  }

  return source;
}

function getActionRunner(button, source) {
  const cachedAction = compiledActions.get(button);

  if (cachedAction?.source === source) {
    return cachedAction.runner;
  }

  let runner;

  try {
    runner = new Function(
      'context',
      `"use strict";
const { actions, button, event, productForm, section, currentProduct, utils } = context;
return (async () => {
${source}
})();`
    );
  } catch (error) {
    throw new Error(`There is a syntax error in the button JavaScript: ${error.message}`);
  }

  compiledActions.set(button, { source, runner });

  return runner;
}

function createContext(button, event, feedback, sourceId) {
  const section = button.closest('.shopify-section');
  const productForm = findNearestProductForm(button);

  return {
    actions: createActions({ button, feedback, sourceId, productForm }),
    button,
    event,
    productForm,
    section,
    currentProduct: {
      variantId: getCurrentVariantId({ button, productForm }),
      sellingPlanId: getCurrentSellingPlanId({ button, productForm }),
      // True when an active subscription-widget / buy-box tier supplied the variant.
      // Lets the cart resolver trust the live selection for ONE-TIME tiers too (which
      // have no selling plan), instead of falling back to the static 1-Pack default.
      variantFromActiveTier: hasValue(
        (getActiveBuyBoxTier(button) || getActiveSubscriptionWidgetTier(button))?.dataset.variantId
      ),
      monthCount: getCurrentMonthCount({ button }),
      quantity: getCurrentQuantity(productForm),
    },
    utils: {
      wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    },
  };
}

async function runBuiltInAction(actionType, config, context) {
  switch (actionType) {
    case 'redirect_to_url':
      return context.actions.redirect(config.redirectUrl);
    case 'open_cart_drawer':
      return context.actions.openCartDrawer();
    case 'add_product_to_cart': {
      const cartOptions = buildCartActionOptions(config, context);
      const shouldDelayDrawerOpen = Boolean(cartOptions.discountCode);
      const restoreCartDrawerAutoOpen = shouldDelayDrawerOpen
        ? temporarilyDisableCartDrawerAutoOpen()
        : null;

      try {
        await context.actions.addToCart({
          ...cartOptions,
          deferDiscountCode: !cartOptions.discountCode,
        });
        return context.actions.openCartDrawer();
      } finally {
        restoreCartDrawerAutoOpen?.();
      }
    }
    case 'express_checkout': {
      const cartOptions = buildCartActionOptions(config, context);
      return context.actions.addToCartAndRedirect(cartOptions, buildCheckoutRedirectUrl(cartOptions.discountCode));
    }
    case 'clear_cart':
      return context.actions.clearCart();
    case 'scroll_to_hash':
      return context.actions.scrollToHash(config.scrollTarget);
    case 'apply_discount_code':
      return context.actions.applyDiscountCode(config.discountCode);
    case 'clear_discount_codes':
      return context.actions.clearDiscountCodes();
    default:
      throw new Error(`Choose a valid button action. Received: ${actionType || 'none'}`);
  }
}

function buildCartActionOptions(config, context) {
  const shouldUseCurrentProductSelection =
    config.useCurrentProductSelection === true && context.productForm instanceof HTMLFormElement;

  // Resolve the selling plan, tracking whether it came from the LIVE selection
  // (the active subscription tier) vs a static config value.
  let selectedSellingPlanId;
  let sellingPlanFromLiveSelection = false;
  if (shouldUseCurrentProductSelection && hasValue(context.currentProduct.sellingPlanId)) {
    selectedSellingPlanId = context.currentProduct.sellingPlanId;
    sellingPlanFromLiveSelection = true;
  } else if (hasValue(config.sellingPlanId)) {
    selectedSellingPlanId = config.sellingPlanId;
  } else {
    selectedSellingPlanId = context.currentProduct.sellingPlanId;
    sellingPlanFromLiveSelection = hasValue(context.currentProduct.sellingPlanId);
  }

  // Resolve the variant. A subscription selling plan is allocated to exactly one
  // variant, so whenever the plan came from the live selection the variant MUST
  // come from the live selection too — otherwise Shopify rejects with
  // "Cannot apply selling plan to variant". Only fall back to the static
  // configured variant when we are NOT carrying a live plan.
  // The widget's ACTIVE TIER is the source of truth for what the customer picked —
  // for subscriptions (variant paired with its plan) AND one-time (variant, no plan).
  // One-time tiers carry no selling plan, so without variantFromActiveTier the resolver
  // would fall through to the static 1-Pack default and add the wrong pack. The static
  // config variant is only a fallback for buttons NOT driven by an active tier.
  const variantFromLiveSelection =
    hasValue(context.currentProduct.variantId) &&
    (shouldUseCurrentProductSelection ||
      sellingPlanFromLiveSelection ||
      context.currentProduct.variantFromActiveTier);

  let selectedVariantId;
  if (variantFromLiveSelection) {
    selectedVariantId = context.currentProduct.variantId;
  } else if (hasValue(config.variantId)) {
    selectedVariantId = config.variantId;
  } else if (hasValue(config.selectedProductVariantId)) {
    selectedVariantId = config.selectedProductVariantId;
  } else {
    selectedVariantId = context.currentProduct.variantId;
  }
  const selectedQuantity = shouldUseCurrentProductSelection
    ? context.currentProduct.quantity
    : config.quantity;
  const items = [
    {
      variantId: selectedVariantId,
      quantity: selectedQuantity,
      sellingPlanId: selectedSellingPlanId,
    },
  ];

  // Free gifts apply only to subscriptions, matched by subscription length (months).
  const matchingGiftBundle = hasValue(selectedSellingPlanId)
    ? getMatchingGiftBundle(config.giftBundles, context.currentProduct.monthCount)
    : null;
  if (matchingGiftBundle) {
    // Gifts do NOT scale with the subscription quantity — one set per order, the way Grüns
    // does it. Buying 3 subscriptions is still one customer receiving one welcome bundle,
    // and it keeps the BXGY's free-unit count matching the gift lines exactly.
    matchingGiftBundle.items.forEach((item) => {
      const giftQuantity = normalizeQuantity(item.quantity || 1);
      items.push({
        ...item,
        quantity: giftQuantity,
        // Line-item markers (underscore-prefixed = hidden from display) so the
        // cart-side reconciler (snippets/dendro-gift-reconcile.liquid) can tell
        // which tier unlocked this gift and what one full set looks like —
        // gifts must scale DOWN or vanish when their subscription leaves the cart.
        properties: {
          _dendro_gift_months: String(item.giftMonths || 1),
          _dendro_gift_qty: String(giftQuantity),
        },
      });
    });
  }

  return {
    items,
    discountCode: matchingGiftBundle?.discountCode || null,
  };
}

function getMatchingGiftBundle(rawBundles, monthCount) {
  const months = parseInt(monthCount, 10);
  if (!Number.isFinite(months) || months <= 0 || !Array.isArray(rawBundles)) {
    return null;
  }

  // Cumulative: a bundle "unlocks from" its length and carries up to longer plans.
  // Accumulate the gifts from every bundle whose length is <= the selected length,
  // deduping by variant so a gift listed in more than one bundle isn't doubled.
  const items = [];
  const seen = new Set();
  let discountCode = null;
  let discountCodeMonths = -1;

  for (const rawBundle of rawBundles) {
    const bundleMonths = parseInt(rawBundle?.months, 10);
    if (!Number.isFinite(bundleMonths) || bundleMonths > months) {
      continue;
    }
    for (const item of parseGiftBundleItems(rawBundle?.variantIds)) {
      if (seen.has(item.variantId)) continue;
      seen.add(item.variantId);
      // Carry the unlocking tier so the line can be stamped for cart-side
      // reconciliation (a gift stays valid only while a plan of at least this
      // length is in the cart).
      items.push({ ...item, giftMonths: bundleMonths });
    }
    // Gifts are cumulative, but the CODE is the SELECTED tier's: use the highest-length
    // matching bundle that carries a code (e.g. 3-month → 3_MONTH_FREE_GIFTS, not the
    // lowest tier's code). That per-tier BXGY frees the full cumulative gift set.
    const code = normalizeDiscountCode(rawBundle?.discountCode);
    if (code && bundleMonths > discountCodeMonths) {
      discountCode = code;
      discountCodeMonths = bundleMonths;
    }
  }

  // Return the bundle when it has gift items OR just a tier discount code — a code-only
  // tier (gifts carried cumulatively from lower bundles) must still surface its code.
  return (items.length || discountCode) ? { items, discountCode } : null;
}

function parseGiftBundleItems(rawVariantIds) {
  if (typeof rawVariantIds !== 'string') {
    return [];
  }

  return rawVariantIds
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [rawVariantId, rawQuantity] = entry.split(':').map((part) => part.trim());
      const variantId = normalizeVariantId(rawVariantId);

      if (!variantId) {
        return null;
      }

      return {
        variantId,
        quantity: normalizeQuantity(rawQuantity || 1),
      };
    })
    .filter(Boolean);
}

function createActions({ button, feedback, sourceId, productForm }) {
  return {
    addCurrentProductToCart: (options = {}) =>
      addToCart({
        button,
        feedback,
        sourceId,
        productForm,
        ...options,
        variantId: hasValue(options.variantId)
          ? options.variantId
          : getCurrentVariantId({ button, productForm }),
        sellingPlanId:
          Object.prototype.hasOwnProperty.call(options, 'sellingPlanId')
            ? hasValue(options.sellingPlanId)
              ? options.sellingPlanId
              : undefined
            : getCurrentSellingPlanId({ button, productForm }),
        quantity: options.quantity ?? getCurrentQuantity(productForm),
      }),
    addToCart: (options = {}) => addToCart({ button, feedback, sourceId, productForm, ...options }),
    addToCartAndRedirect: async (options = {}, url = Theme.routes.cart_url) => {
      await addToCart({
        button,
        feedback,
        sourceId,
        productForm,
        ...options,
        skipCartRefresh: true,
      });
      redirectTo(url);
    },
    redirect: (url) => redirectTo(url),
    open: (url, target = '_blank') => openUrl(url, target),
    openCartDrawer: () => openCartDrawer(),
    scrollToHash: (hash) => scrollToHash(hash),
    clearCart: () => clearCart(button, sourceId),
    applyDiscountCode: (discountCode) => applyDiscountCode(button, sourceId, discountCode),
    clearDiscountCodes: () => clearDiscountCodes(button, sourceId),
    showMessage: (message) => showFeedback(feedback, message, 'info'),
    clearMessage: () => clearFeedback(feedback),
  };
}

async function addToCart({
  button,
  feedback,
  sourceId,
  productForm,
  items,
  variantId,
  quantity = 1,
  sellingPlanId,
  properties,
  discountCode,
  deferDiscountCode = false,
  skipCartRefresh = false,
}) {
  const normalizedItems = normalizeCartItems({
    button,
    productForm,
    items,
    variantId,
    quantity,
    sellingPlanId,
    properties,
  });
  const sections = getCartSectionIds();

  const body = {
    items: normalizedItems,
  };

  if (sections.length) {
    body.sections = sections.join(',');
    body.sections_url = window.location.pathname;
  }

  // Shopify will not merge a line that already carries a discount allocation with a freshly
  // added identical one, so a second purchase of the same (variant, plan) lands as a SECOND
  // line at quantity 1 instead of taking the first line to 2 — and the gifts stop tracking
  // the real subscription count. Bump any line that already exists, and only /cart/add the
  // genuinely new ones (still one atomic bulk call, so an unavailable gift can't half-add).
  const existingLines = await getExistingCartLines();
  const toBump = [];
  const toAdd = [];

  normalizedItems.forEach((item) => {
    const match = existingLines.get(cartLineSignature(item.id, item.selling_plan));
    // A free gift is one per order, never per click: a gift already in the cart is left alone
    // (bumping it is what produced qty-2 Ear Plugs when a shopper re-added after changing plan).
    // The shared reconciler below owns gift quantities.
    if (match && isGiftCartItem(item)) return;
    if (match) toBump.push({ key: match.key, quantity: match.quantity + normalizeQuantity(item.quantity) });
    else toAdd.push(item);
  });

  let data = null;

  if (toAdd.length) {
    let response = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', { body: JSON.stringify({ ...body, items: toAdd }) })
    );
    data = await response.json();

    // One refused gift (Ear Plugs is stock-tracked) rejects the whole bulk add, subscription
    // included. A free gift must never cost the sale: retry with the paid lines alone and let
    // the gift reconciler top the gifts up afterwards.
    if ((!response.ok || data?.status) && toAdd.some(isGiftCartItem)) {
      const paidOnly = toAdd.filter((item) => !isGiftCartItem(item));
      if (paidOnly.length) {
        response = await fetch(
          Theme.routes.cart_add_url,
          fetchConfig('json', { body: JSON.stringify({ ...body, items: paidOnly }) })
        );
        data = await response.json();
      }
    }

    if (!response.ok || data?.status) {
      throw new Error(data?.description || data?.message || 'Unable to add this item to the cart.');
    }
  }

  for (const line of toBump) {
    const changeBody = { id: line.key, quantity: line.quantity };
    if (sections.length) {
      changeBody.sections = sections.join(',');
      changeBody.sections_url = window.location.pathname;
    }
    const changeResponse = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', { body: JSON.stringify(changeBody) })
    );
    const changeData = await changeResponse.json();
    if (!changeResponse.ok || changeData?.status) {
      throw new Error(changeData?.description || changeData?.message || 'Unable to update the cart.');
    }
    data = changeData; // the last response carries the freshest sections for the re-render
  }

  // Converge the gift lines to the rule BEFORE the drawer renders or checkout opens — this
  // covers the express-checkout redirect too, which never fires a cart event.
  const giftResult = await reconcileGifts(sections);
  if (giftResult?.sections) data = { ...(data || {}), sections: giftResult.sections };

  const normalizedDiscountCode = normalizeDiscountCode(discountCode);
  const quantitySelector = productForm?.querySelector('quantity-selector-component');
  const primaryItem = normalizedItems[0];
  const shouldFetchCart = Boolean(quantitySelector?.setCartQuantity);
  const canDeferDiscountCode = deferDiscountCode && normalizedDiscountCode && !skipCartRefresh;
  let discountUpdate = null;

  if (normalizedDiscountCode && !canDeferDiscountCode) {
    // The paid line is already in the cart: a failed discount call must not abort the sale.
    discountUpdate = await applyDiscountCode(button, sourceId, normalizedDiscountCode, sections).catch((error) => {
      console.warn('[Super Button] discount code could not be applied', error);
      return null;
    });
  }

  if (skipCartRefresh) {
    clearFeedback(feedback);
    return { cart: null, data };
  }

  clearFeedback(feedback);

  const renderedSections = discountUpdate?.sections || data?.sections;
  const itemCount = extractCartItemCountFromSections(renderedSections);

  button.dispatchEvent(
    new CartAddEvent({}, sourceId, {
      source: 'button-action',
      itemCount,
      variantId: String(primaryItem.id),
      sections: renderedSections,
    })
  );

  if (shouldFetchCart) {
    void fetchCart()
      .then((cart) => {
        if (!quantitySelector?.setCartQuantity) return;

        const matchingItem = cart.items?.find((item) => String(item.variant_id) === String(primaryItem.id));
        quantitySelector.setCartQuantity(matchingItem?.quantity || 0);
      })
      .catch((error) => {
        console.error('Unable to refresh cart quantity after button action:', error);
      });
  }

  if (canDeferDiscountCode) {
    void applyDiscountCode(button, sourceId, normalizedDiscountCode, sections)
      .catch((error) => {
        console.error('Discount code application failed after cart add:', error);
      });
  }

  return { cart: null, data };
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('The cart updated, but the new cart state could not be loaded.');
  }

  return response.json();
}

function extractCartItemCountFromSections(sections) {
  if (!sections || typeof sections !== 'object') {
    return 0;
  }

  for (const html of Object.values(sections)) {
    if (typeof html !== 'string' || !html) {
      continue;
    }

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const itemCount = documentFragment.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
    if (!itemCount) {
      continue;
    }

    const parsedItemCount = Number.parseInt(itemCount, 10);
    if (Number.isFinite(parsedItemCount)) {
      return parsedItemCount;
    }
  }

  return 0;
}

async function clearCart(button, sourceId) {
  const response = await fetch('/cart/clear.js', fetchConfig('json', { body: JSON.stringify({}) }));
  const cart = await response.json();

  button.dispatchEvent(
    new CartAddEvent(cart, sourceId, {
      source: 'button-action',
      itemCount: 0,
    })
  );

  return cart;
}

function openCartDrawer() {
  const cartDrawer = document.querySelector('cart-drawer-component');

  if (cartDrawer && typeof cartDrawer.open === 'function') {
    cartDrawer.open();
    return;
  }

  window.location.assign(Theme.routes.cart_url);
}

function temporarilyDisableCartDrawerAutoOpen() {
  const cartDrawer = document.querySelector('cart-drawer-component');

  if (!(cartDrawer instanceof HTMLElement)) {
    return null;
  }

  const hadAutoOpen = cartDrawer.hasAttribute('auto-open');

  if (hadAutoOpen) {
    cartDrawer.removeAttribute('auto-open');
  }

  return () => {
    if (hadAutoOpen) {
      cartDrawer.setAttribute('auto-open', '');
    }
  };
}

async function applyDiscountCode(button, sourceId, discountCode, sections = []) {
  const normalizedCode = String(discountCode || '').trim();

  if (!normalizedCode) {
    throw new Error('Discount code is required.');
  }

  const body = {
    discount: normalizedCode,
  };

  if (sections.length) {
    body.sections = sections;
    body.sections_url = window.location.pathname;
  }

  const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', {
    body: JSON.stringify(body),
  }));
  const data = await response.json();

  if (!response.ok) {
    throw new Error('Unable to apply discount code.');
  }

  document.dispatchEvent(new DiscountUpdateEvent(data, sourceId));

  // Observability (non-blocking): Shopify returns HTTP 200 even when a code is invalid,
  // non-existent, or not-yet-applicable, so response.ok is NOT proof the discount landed.
  // Warn loudly when the code we sent did not actually apply, so a missing/misconfigured
  // gift discount is caught in testing instead of silently shipping a full-price "free"
  // gift. Never throws — a discount problem must not block the cart add.
  try {
    const applied = (data?.discount_codes || []).find(
      (entry) => String(entry?.code || '').toLowerCase() === normalizedCode.toLowerCase()
    );
    if (!applied || applied.applicable === false) {
      console.warn(
        `[Super Button] Discount code "${normalizedCode}" did not apply — the free gift may be full price. ` +
          'Check that this Buy-X-Get-Y discount exists, is active, and its conditions match the cart.'
      );
    }
  } catch (_) {
    /* observability only — never affects the cart */
  }

  return data;
}

async function clearDiscountCodes(button, sourceId) {
  const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', {
    body: JSON.stringify({ discount: '' }),
  }));
  const data = await response.json();

  if (!response.ok) {
    throw new Error('Unable to clear discount codes.');
  }

  document.dispatchEvent(new DiscountUpdateEvent(data, sourceId));
  return data;
}

function findNearestProductForm(button) {
  const localForm =
    button.closest('product-form-component')?.querySelector('form[action*="/cart/add"]') ||
    button.closest('form[action*="/cart/add"]');

  if (localForm instanceof HTMLFormElement) {
    return localForm;
  }

  const section = button.closest('.shopify-section');
  return section?.querySelector('product-form-component form[action*="/cart/add"]') || null;
}

function getCurrentVariantId({ button, productForm }) {
  // A subscription tier defines an atomic (variant, selling plan) pair — each
  // selling plan is allocated to exactly ONE variant. When a subscription tier
  // is active, trust its variant over the product form's [name="id"], which can
  // be left stale by the variant-picker / product-form re-render race. Pairing
  // the live plan with a stale variant is what triggers Shopify's
  // "Cannot apply selling plan to variant" rejection.
  const activeTier = getActiveBuyBoxTier(button) || getActiveSubscriptionWidgetTier(button);
  if (activeTier?.dataset.sellingPlanId && activeTier.dataset.variantId) {
    return activeTier.dataset.variantId;
  }

  const formValue = productForm?.querySelector('[name="id"]')?.value;

  if (formValue) {
    return formValue;
  }

  return activeTier?.dataset.variantId || null;
}

function getCurrentSellingPlanId({ button, productForm }) {
  // Mirror getCurrentVariantId: a subscription tier is an atomic (variant, plan)
  // pair, so when a tier is active trust ITS plan over the product form's
  // [name="selling_plan"], which the variant-picker / form re-render can leave
  // stale. Pairing a stale form plan with the fresh tier variant is what triggers
  // Shopify's "Cannot apply selling plan to variant" rejection.
  const activeTier = getActiveBuyBoxTier(button) || getActiveSubscriptionWidgetTier(button);
  if (activeTier?.dataset.sellingPlanId && activeTier.dataset.variantId) {
    return activeTier.dataset.sellingPlanId;
  }

  const formValue = productForm?.querySelector('[name="selling_plan"]')?.value;

  if (formValue) {
    return formValue;
  }

  const linkedFormId = productForm?.id;
  if (linkedFormId) {
    const linkedSellingPlanInput = document.querySelector(`[name="selling_plan"][form="${linkedFormId}"]`);
    if (linkedSellingPlanInput instanceof HTMLInputElement && linkedSellingPlanInput.value) {
      return linkedSellingPlanInput.value;
    }
  }

  const activeBuyBoxTier = getActiveBuyBoxTier(button);
  if (activeBuyBoxTier?.dataset.sellingPlanId) {
    return activeBuyBoxTier.dataset.sellingPlanId;
  }

  const activeSubscriptionTier = getActiveSubscriptionWidgetTier(button);
  return activeSubscriptionTier?.dataset.sellingPlanId || null;
}

function getCurrentQuantity(productForm) {
  return normalizeQuantity(productForm?.querySelector('[name="quantity"]')?.value || 1);
}

// Subscription length (months) of the active tier — used to match free-gift bundles.
// One-time tiers carry pack-count, not month-count, so this is 0 for them (no gifts).
function getCurrentMonthCount({ button }) {
  const activeTier = getActiveBuyBoxTier(button) || getActiveSubscriptionWidgetTier(button);
  const mc = activeTier && activeTier.dataset ? parseInt(activeTier.dataset.monthCount, 10) : NaN;
  return Number.isFinite(mc) ? mc : 0;
}

function getActiveBuyBoxTier(button) {
  const buyBoxRoot = button.closest('[id^="bb-section-"]');

  if (!(buyBoxRoot instanceof HTMLElement)) {
    return null;
  }

  const subscriptionWidget = buyBoxRoot.querySelector('.bb-subscription-widget');
  const activeMode = subscriptionWidget?.dataset.mode;

  if (activeMode) {
    const activeTierForMode = buyBoxRoot.querySelector(`.bb-sw-tier--active[data-card-type="${activeMode}"]`);
    if (activeTierForMode instanceof HTMLElement) {
      return activeTierForMode;
    }
  }

  const activeTier = buyBoxRoot.querySelector('.bb-sw-tier--active');
  return activeTier instanceof HTMLElement ? activeTier : null;
}

function getActiveSubscriptionWidgetTier(button) {
  const sectionRoot = button.closest('.shopify-section');

  if (!(sectionRoot instanceof HTMLElement)) {
    return null;
  }

  const subscriptionWidget = sectionRoot.querySelector('subscription-widget');
  if (!(subscriptionWidget instanceof HTMLElement)) {
    return null;
  }

  if (subscriptionWidget.dataset.mode === 'subscribe') {
    const activeSubscribeTier = subscriptionWidget.querySelector('.sw-card--subscribe .sw-tier--active');
    if (activeSubscribeTier instanceof HTMLElement) {
      return activeSubscribeTier;
    }
  }

  if (subscriptionWidget.dataset.mode === 'onetime') {
    const activeOnetimeTier = subscriptionWidget.querySelector('.sw-tiers--onetime .sw-tier--active');
    if (activeOnetimeTier instanceof HTMLElement) {
      return activeOnetimeTier;
    }

    return null;
  }

  const activeTier = subscriptionWidget.querySelector('.sw-tier--active');
  return activeTier instanceof HTMLElement ? activeTier : null;
}

function normalizeVariantId(variantId) {
  if (variantId == null || variantId === '') return null;

  return String(variantId).trim();
}

function normalizeSellingPlanId(sellingPlanId) {
  if (sellingPlanId == null || sellingPlanId === '') return null;

  return String(sellingPlanId).trim();
}

function normalizeDiscountCode(discountCode) {
  if (discountCode == null || discountCode === '') return null;

  return String(discountCode).trim();
}

function normalizeQuantity(quantity) {
  const parsedQuantity = Number.parseInt(String(quantity), 10);

  if (!Number.isFinite(parsedQuantity) || parsedQuantity < 1) {
    return 1;
  }

  return parsedQuantity;
}

function hasValue(value) {
  return value != null && String(value).trim() !== '';
}

function normalizeProperties(properties) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
    return null;
  }

  // Object.create(null) + skipping prototype keys avoids any prototype-pollution
  // path from a custom-JS-supplied properties object.
  const PROTO_KEYS = ['__proto__', 'constructor', 'prototype'];
  return Object.entries(properties).reduce((result, [key, value]) => {
    if (value == null) return result;
    if (PROTO_KEYS.includes(key)) return result;

    result[String(key)] = String(value);
    return result;
  }, Object.create(null));
}

function normalizeCartItems({ button, productForm, items, variantId, quantity, sellingPlanId, properties }) {
  if (Array.isArray(items) && items.length) {
    const normalizedItems = items
      .map((item) => normalizeCartItem({
        button,
        productForm,
        variantId: item?.variantId,
        quantity: item?.quantity,
        sellingPlanId: item?.sellingPlanId,
        properties: item?.properties,
        allowVariantFallback: false,
      }))
      .filter(Boolean);

    if (normalizedItems.length) {
      return normalizedItems;
    }
  }

  const normalizedItem = normalizeCartItem({
    button,
    productForm,
    variantId,
    quantity,
    sellingPlanId,
    properties,
    allowVariantFallback: true,
  });

  return [normalizedItem];
}

function normalizeCartItem({
  button,
  productForm,
  variantId,
  quantity,
  sellingPlanId,
  properties,
  allowVariantFallback,
}) {
  const resolvedVariantId = hasValue(variantId)
    ? variantId
    : allowVariantFallback
      ? getCurrentVariantId({ button, productForm })
      : null;
  const normalizedVariantId = normalizeVariantId(resolvedVariantId);

  if (!normalizedVariantId) {
    if (!allowVariantFallback) {
      return null;
    }

    if (!productForm) {
      throw new Error('Variant ID is required here. On non-product pages, choose a Selected product or fill Variant ID in the button settings.');
    }

    throw new Error('Choose a product variant, pick a Selected product, or fill Variant ID in the button settings.');
  }

  const normalizedQuantity = normalizeQuantity(quantity);
  const normalizedSellingPlanId =
    sellingPlanId === undefined || !hasValue(sellingPlanId)
      ? allowVariantFallback
        ? normalizeSellingPlanId(getCurrentSellingPlanId({ button, productForm }))
        : null
      : normalizeSellingPlanId(sellingPlanId);
  const normalizedProperties = normalizeProperties(properties);

  return {
    id: normalizedVariantId,
    quantity: normalizedQuantity,
    ...(normalizedSellingPlanId ? { selling_plan: normalizedSellingPlanId } : {}),
    ...(normalizedProperties ? { properties: normalizedProperties } : {}),
  };
}

/**
 * A cart line is "the same line" only when the variant AND the selling plan match — a 1-Pack
 * monthly and a 2-Pack bi-monthly stay separate, which is what the customer needs to see.
 * @param {number|string} variantId
 * @param {number|string|undefined} sellingPlanId
 */
function cartLineSignature(variantId, sellingPlanId) {
  return `${variantId}::${sellingPlanId || 'none'}`;
}

/**
 * Current cart lines keyed by that signature. Returns an empty map on any failure so the
 * caller falls back to a plain add — never block a purchase on this lookup.
 * @returns {Promise<Map<string, {key: string, quantity: number}>>}
 */
async function getExistingCartLines() {
  const lines = new Map();
  try {
    const response = await fetch(`${Theme.routes.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return lines;
    const cart = await response.json();
    (cart?.items || []).forEach((item) => {
      const signature = cartLineSignature(item.variant_id, item.selling_plan_allocation?.selling_plan?.id);
      // First match wins: if the cart is already split, bumping the earliest line reunifies it.
      if (!lines.has(signature)) lines.set(signature, { key: item.key, quantity: item.quantity });
    });
  } catch (error) {
    console.warn('[super-button] cart lookup failed, adding as new lines', error);
  }
  return lines;
}

/**
 * A cart item this engine stamped as a free gift (see buildCartActionOptions).
 * @param {{properties?: Record<string, string>}} item
 */
function isGiftCartItem(item) {
  return Boolean(item?.properties && Object.prototype.hasOwnProperty.call(item.properties, '_dendro_gift_months'));
}

/**
 * Runs the shared gift reconciler (snippets/dendro-gift-reconcile.liquid) and waits for it.
 * Returns its result only when it changed the cart, so the caller can render its sections.
 * Never throws — a gift problem must not block the purchase.
 * @param {string[]} sections
 */
async function reconcileGifts(sections) {
  const api = window.DendroGifts;
  if (!api || typeof api.reconcile !== 'function') return null;
  try {
    // Bounded: a stalled gift request must never hold the paid add, the drawer or the express redirect.
    const result = await Promise.race([
      api.reconcile({ sections, silent: true, topUp: true }),
      new Promise((resolve) => setTimeout(() => resolve(null), 3500)),
    ]);
    return result?.changed ? result : null;
  } catch (error) {
    console.warn('[Super Button] gift reconcile failed', error);
    return null;
  }
}

function getCartSectionIds() {
  const sectionIds = new Set();

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.add(item.dataset.sectionId);
    }
  });

  return Array.from(sectionIds);
}

function redirectTo(url) {
  const targetUrl = normalizeUrl(url);
  window.location.assign(targetUrl);
}

function openUrl(url, target = '_blank') {
  const targetUrl = normalizeUrl(url);
  window.open(targetUrl, target, target === '_blank' ? 'noopener,noreferrer' : undefined);
}

function scrollToHash(hash) {
  if (!hash) return;

  const normalizedHash = hash.startsWith('#') ? hash : `#${hash}`;
  const target = document.querySelector(normalizedHash);

  if (!target) {
    throw new Error(`No element was found for ${normalizedHash}.`);
  }

  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  history.replaceState(null, '', normalizedHash);
}

function normalizeUrl(url) {
  if (typeof url !== 'string' || url.trim() === '') {
    throw new Error('A valid URL is required.');
  }

  const trimmedUrl = url.trim();

  // Relative paths / in-page anchors are always safe.
  if (trimmedUrl.startsWith('#') || trimmedUrl.startsWith('/')) {
    return trimmedUrl;
  }

  // Allowlist URL schemes (safer than denylisting only javascript:): reject
  // javascript:, data:, vbscript:, blob:, etc. — only navigable web schemes pass.
  const scheme = trimmedUrl.match(/^([a-z][a-z0-9+.-]*):/i)?.[1]?.toLowerCase();
  const ALLOWED_SCHEMES = ['http', 'https', 'mailto', 'tel'];
  if (scheme && !ALLOWED_SCHEMES.includes(scheme)) {
    throw new Error(`"${scheme}:" URLs are not allowed in button actions.`);
  }

  const parsedUrl = new URL(trimmedUrl, window.location.origin);
  return parsedUrl.toString();
}

function buildCheckoutRedirectUrl(discountCode) {
  const normalizedDiscountCode = normalizeDiscountCode(discountCode);
  if (!normalizedDiscountCode) {
    return '/checkout';
  }

  return `/discount/${encodeURIComponent(normalizedDiscountCode)}?redirect=/checkout`;
}


// The feedback span follows the button after one or more <script> config nodes (the Super
// Button emits two: the action script and the gift config). Walk past scripts to the first
// feedback node — taking a fixed "next-next sibling" wrote ATC errors into the gift-config
// <script>, so every add-to-cart failure was invisible.
function getFeedbackNode(button) {
  let node = button.nextElementSibling;
  while (node && node.tagName === 'SCRIPT') node = node.nextElementSibling;
  return node instanceof HTMLElement && node.matches(FEEDBACK_SELECTOR) ? node : null;
}

function initializeButtonAnimations() {
  const init = () => {
    document.querySelectorAll('[data-super-button-animation]').forEach((element) => {
      if (!(element instanceof HTMLElement) || animatedButtons.has(element)) return;
      animatedButtons.add(element);
      setupAnimation(element);
    });
  };

  init();

  const observer = new MutationObserver(init);
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

function setupAnimation(element) {
  if (prefersReducedMotion() || !shouldAnimateOnCurrentDevice(element)) {
    return;
  }

  const duration = Number.parseInt(element.dataset.superButtonAnimationDuration || '300', 10);
  const delay = Number.parseInt(element.dataset.superButtonAnimationDelay || '0', 10);

  element.style.setProperty('--super-button-animation-duration', `${duration}ms`);
  element.style.setProperty('--super-button-animation-delay', `${delay}ms`);
  element.classList.add('super-button-animation-pending');

  if ((element.dataset.superButtonAnimationTrigger || 'on_viewport_enter') === 'on_load') {
    requestAnimationFrame(() => runAnimation(element));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    const entry = entries[0];
    if (!entry?.isIntersecting) return;

    runAnimation(element);

    if (element.dataset.superButtonAnimationOnce !== 'false') {
      observer.disconnect();
    }
  }, { threshold: 0.2 });

  observer.observe(element);
}

function runAnimation(element) {
  element.classList.remove('super-button-animation-pending');
  element.classList.remove('super-button-animation-active');
  void element.offsetWidth;
  element.classList.add('super-button-animation-active');
}

function shouldAnimateOnCurrentDevice(element) {
  const width = window.innerWidth;

  if (width < 750) {
    return element.dataset.superButtonAnimationMobile !== 'false';
  }

  if (width < 990) {
    return element.dataset.superButtonAnimationTablet !== 'false';
  }

  return element.dataset.superButtonAnimationDesktop !== 'false';
}

function showFeedback(feedbackNode, message, status = 'info') {
  if (!feedbackNode) return;

  feedbackNode.setAttribute('role', status === 'error' ? 'alert' : 'status');
  feedbackNode.textContent = message;
  feedbackNode.dataset.status = status;
  feedbackNode.hidden = false;
}

function clearFeedback(feedbackNode) {
  if (!feedbackNode) return;

  feedbackNode.hidden = true;
  feedbackNode.textContent = '';
  delete feedbackNode.dataset.status;
}

function formatErrorMessage(error) {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Something went wrong while running this button action.';
}
